import React, { type JSX } from 'react';
import { useSelector } from 'react-redux';
import {
  determineIfAllowedScopes,
  Permissions,
  SharedRootStore,
} from '@cowbell/data';

type ChildrenFunction = (isAllowed: boolean) => JSX.Element;

interface AllowProps {
  readonly allow: Permissions[] | 'any';
  readonly requireAll?: boolean;
  readonly mergePropsWithChildren?: boolean;
  readonly children: JSX.Element | ChildrenFunction | JSX.Element[];
  readonly onClick?: () => void;
}

export const Allow: React.FC<AllowProps> = ({
  allow,
  requireAll = false,
  mergePropsWithChildren = true,
  children,
  ...props
}) => {
  const scopes = useSelector((state: SharedRootStore) => state.scopes);
  const isAllowed = determineIfAllowedScopes(scopes, allow, requireAll);

  if (typeof children === 'function') return children(isAllowed);
  if (!isAllowed) return null;

  if (mergePropsWithChildren) {
    return (
      <>
        {React.Children.map(children as JSX.Element, (child) => {
          if (!React.isValidElement<Record<string, unknown>>(child))
            return child;
          return React.cloneElement(child, { ...props, ...child.props });
        })}
      </>
    );
  }

  return <>{children}</>;
};
